import { Icon } from "icons";
import { observer } from "mobx-react-lite";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from "react";
import { useTranslation } from "react-i18next";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  cn,
  useShallowEffect,
} from "ui";
import { useDebouncedCallback } from "use-debounce";
import { assert, getMuscleNamesByGroups } from "utils";

import { ExerciseComboboxItem } from "src/components/ExerciseComboboxItem";
import { CreateExerciseDialog } from "src/exercise/CreateExerciseDialog";
import {
  type ContentProps,
  comboboxMuscleGroups,
} from "src/workouts/ExerciseCombobox";
import { useExerciseSearchContext } from "src/workouts/ExerciseSearchContext";

export const Content = observer(function Content(props: ContentProps) {
  const { t } = useTranslation();
  const exerciseSearchContext = useExerciseSearchContext();
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const [selectedMuscleGroups, setSelectedMuscleGroups] = useState<string[]>(
    [],
  );
  const [localFilters, setLocalFilters] = useState({
    query: "",
  });

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const numberOfPagesRef = useRef(
    exerciseSearchContext.query.data?.pages.length || 0,
  );

  const [popoverAnchor, setPopoverAnchor] = useState<
    RefObject<HTMLDivElement | null>
  >({ current: null });
  const saveRef = useCallback((ref: HTMLDivElement | null) => {
    setPopoverAnchor({ current: ref });
  }, []);

  // Debounce all filter updates
  const debouncedSetFilters = useDebouncedCallback(
    (filters: { query: string }) => {
      exerciseSearchContext.setFilters(filters);
    },
    80,
  );

  function toggleMuscleGroup(groupName: string) {
    setSelectedMuscleGroups((prev) => {
      const next = prev.includes(groupName)
        ? prev.filter((n) => {
            return n !== groupName;
          })
        : [...prev, groupName];
      const muscles = getMuscleNamesByGroups(next);
      exerciseSearchContext.setFilters({ muscles });
      return next;
    });
  }

  // Scroll to top when filters change
  useShallowEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [
    exerciseSearchContext.filters.query,
    exerciseSearchContext.filters.muscles,
  ]);

  const pages = exerciseSearchContext.query.data?.pages || [];

  useEffect(() => {
    if (
      !exerciseSearchContext.query.data ||
      numberOfPagesRef.current !== exerciseSearchContext.query.data.pages.length
    ) {
      return;
    }

    const firstPage = exerciseSearchContext.query.data.pages[0];

    if (!firstPage) {
      return;
    }

    // Selects/Highlights first element in CMDK (niezłe gnoje z cmdk tbf)
    setTimeout(() => {
      searchInputRef.current?.dispatchEvent(
        new KeyboardEvent("keydown", {
          key: "ArrowUp",
          code: "ArrowUp",
          keyCode: 38,
          metaKey: true,
          bubbles: true,
          cancelable: true,
        }),
      );
    }, 50);
  }, [exerciseSearchContext.query.data]);

  useEffect(() => {
    numberOfPagesRef.current =
      exerciseSearchContext.query.data?.pages.length || 0;
  }, [exerciseSearchContext.query.data]);

  const exercises =
    pages.flatMap((page) => {
      return page.currentPage;
    }) ?? null;

  const isFetching = exerciseSearchContext.query.isFetching;
  const hasExercises = exercises && exercises.length > 0;
  const showInputSpinner = isFetching && hasExercises;
  const showListLoader = isFetching && !hasExercises;

  const loader = (
    <div className="w-full flex justify-center">
      <Icon name="tabler-loader-2" className="animate-spin" />
    </div>
  );

  function tryFetchNextPage() {
    if (isFetching || !exerciseSearchContext.query.hasNextPage) {
      return;
    }

    exerciseSearchContext.query.fetchNextPage();
  }

  return (
    <>
      <Command
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            props.onClose();
          }
        }}
        shouldFilter={false}
        ref={saveRef}
        onItemHighlighted={(itemId) => {
          setHighlightedId(itemId);
          const isLastItemHighlighted =
            itemId === exercises?.[exercises.length - 1]?.id;

          if (isLastItemHighlighted) {
            tryFetchNextPage();
          }
        }}
      >
        <CommandInput
          placeholder={t("exerciseCombobox.searchPlaceholder")}
          value={localFilters.query}
          onValueChange={(value) => {
            setLocalFilters((prev) => {
              const newFilters = { ...prev, query: value };
              debouncedSetFilters(newFilters);
              return newFilters;
            });
          }}
          ref={searchInputRef}
          right={
            <div className="flex items-center gap-2">
              {showInputSpinner && (
                <Icon
                  name="tabler-loader-2"
                  className="h-4 w-4 animate-spin opacity-50"
                />
              )}
              <CreateExerciseDialog
                buttonProps={{
                  variant: "outline",
                  className: "text-xs h-4 w-auto rounded-[8px]",
                }}
                onCreateExercise={(exercise) => {
                  props.onChange({ ...exercise, thumbnail: null });
                }}
              />
            </div>
          }
        />
        <div className="p-1 border-b border-zinc-200">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-medium text-zinc-500">
              {t("exerciseCombobox.filterByMuscleGroup")}
            </span>
            {selectedMuscleGroups.length > 0 && (
              <button
                type="button"
                onClick={() => {
                  setSelectedMuscleGroups([]);
                  exerciseSearchContext.setFilters({ muscles: [] });
                }}
                className="text-xs text-zinc-400 hover:text-zinc-600 cursor-pointer"
              >
                {t("exerciseCombobox.clearAll")}
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-1">
            {comboboxMuscleGroups.map((group) => {
              const isActive = selectedMuscleGroups.includes(group.name);
              return (
                <button
                  key={group.name}
                  type="button"
                  onClick={() => {
                    return toggleMuscleGroup(group.name);
                  }}
                  className={cn(
                    "px-2 py-0.5 rounded-md text-xs font-medium transition-colors",
                    "border cursor-pointer select-none",
                    {
                      "bg-zinc-800 text-white border-zinc-800": isActive,
                      "bg-zinc-50 text-zinc-600 border-zinc-200 hover:bg-zinc-100 hover:border-zinc-300":
                        !isActive,
                    },
                  )}
                >
                  {t(`muscleGroups.${group.name}`)}
                </button>
              );
            })}
          </div>
        </div>
        <CommandList>
          <CommandGroup
            className="max-h-[260px] min-h-[260px] overflow-y-scroll"
            ref={scrollRef}
            onScroll={(e) => {
              const obj = e.currentTarget;

              const scrolledToBottom =
                Math.round(obj.scrollTop) ===
                Math.round(obj.scrollHeight) - Math.round(obj.offsetHeight);

              if (!scrolledToBottom) {
                return;
              }

              tryFetchNextPage();
            }}
          >
            {!hasExercises && !isFetching && (
              <CommandEmpty>{t("selectExercise.noResults")}</CommandEmpty>
            )}

            {exercises && exercises.length > 0 ? (
              <>
                {exercises.map((exercise) => {
                  return (
                    <CommandItem
                      className="p-0"
                      key={exercise.id}
                      value={exercise.id}
                      onSelect={(currentValue) => {
                        const exercise = exercises.find(
                          (a) => a.id === currentValue,
                        );
                        assert(exercise, "No athlete");
                        props.onChange(exercise);
                      }}
                    >
                      <ExerciseComboboxItem
                        showPreview={highlightedId === exercise.id}
                        exercise={exercise}
                        selected={props.value?.id === exercise.id}
                        loadingExerciseId={props.loadingExerciseId}
                        imageClassName={cn("h-7 w-7 rounded-[4px]")}
                        popoverAnchorRef={
                          popoverAnchor?.current
                            ? { current: popoverAnchor.current }
                            : undefined
                        }
                        exercisePreviewSide={props.exercisePreviewSide}
                        exerciseNameClassName={props.exerciseNameClassName}
                        autoPlayVideoAfter={props.autoPlayVideoAfter}
                      />
                    </CommandItem>
                  );
                })}
              </>
            ) : (
              showListLoader && loader
            )}
          </CommandGroup>
        </CommandList>
      </Command>
    </>
  );
});
